/**
 * The icons an operator may bind to a rule, and what to call them.
 *
 * WHY THIS EXISTS. The icon vocabulary was written out by hand in three places
 * — the renderer's own map in `IconDefinition.tsx`, the picker in
 * `IconDefinitionRules.tsx`, and `ELEVATOR_ICONS_MAP` in
 * `IconsGetIconByName.tsx`. Nothing kept them in step, so the two copies
 * silently stopped covering icons the renderer had gained: the entire
 * `elGoal*` hall-call family was undrawable from the very screen whose job is
 * choosing it, and `elGoalHallCall` was registered by the renderer and
 * referenced by nothing at all.
 *
 * This file is the one list. It carries IDs and labels only — deliberately no
 * assets, because 26 of the renderer's entries resolve differently per theme
 * and duplicating that resolution would recreate the drift one layer down.
 * Consumers that need the asset call `buildIconAssetMap(theme)`.
 *
 * `iconRegistry.test.ts` asserts every ID here resolves in that map, so an ID
 * added to one and not the other fails rather than rendering nothing.
 */

/** One selectable icon. */
export interface IconRegistryEntry {
  /** Key into `buildIconAssetMap(theme)[liftType]`. */
  id: string;
  /** Operator-facing name. */
  label: string;
}

/**
 * Elevator icons offered for binding.
 *
 * Not every key in the renderer's map — the door-state and action glyphs it
 * draws from `bitmaskToIcon` are derived, not chosen — but every icon a rule or
 * a hall-call binding can point at.
 */
export const ELEVATOR_ICON_REGISTRY: IconRegistryEntry[] = [
  { id: "elUp", label: "Up" },
  { id: "elDown", label: "Down" },
  { id: "elClose", label: "Door closed" },
  { id: "elOpen", label: "Door open" },
  { id: "elFrontCloseRearClose", label: "Front close, rear close" },
  { id: "elFrontCloseRearOpen", label: "Front close, rear open" },
  { id: "elFrontOpenRearClose", label: "Front open, rear close" },
  { id: "elFrontOpenRearOpen", label: "Front open, rear open" },
  // Hall-call indicators. Absent from the picker until this registry existed,
  // which is the concrete cost of the duplication described above.
  { id: "elGoalHallCall", label: "Hall call" },
  { id: "elGoalGreen", label: "Hall call up (green)" },
  { id: "elGoalRed", label: "Hall call down (red)" },
  { id: "elGoalBlue", label: "Hall call (blue)" },
  { id: "elGoalGold", label: "Hall call (gold)" },
];

/** Escalator icons offered for binding. */
export const ESCALATOR_ICON_REGISTRY: IconRegistryEntry[] = [
  { id: "esUp", label: "Up" },
  { id: "esDown", label: "Down" },
  { id: "esStopped", label: "Stopped" },
];
